import { waitForProjectServices } from '../hooks/useProjectServices';
import { serviceState } from '../services/SandboxService';
import { isPreviewReady, isStoppedOnPurpose, serviceEntryFor } from './previewReadiness';

/**
 * Where a service is when the Preview opens it, from the shared store, WAITING for the store's first answer. On a
 * cold page load the store has no answer yet (its first calls can even fail while the session settles), and "not
 * known yet" used to be read as "no session": the Preview posted /api/render and started an app the user had
 * stopped. Every Preview path that opens a service (tab creation, the loader, a failed iframe load, files landing)
 * asks this first, and none of them starts it (decidePreviewOpen).
 *
 * Resolves to `{ state, entry }`, `state` one of ready | stopped | building | running | crashed | failed | absent
 * (the runner has no session), or null when vfs-service cannot tell (then the caller does what it did before).
 * @param {string} projectId
 * @param {string} serviceName
 * @param {{ maxAgeMs?: number, timeoutMs?: number, retryMs?: number }} [options] `maxAgeMs` 0: always a fresh answer
 */
export async function previewRunStateFor(projectId, serviceName, options = {}) {
  const answer = await waitForProjectServices(projectId, options).catch(() => undefined);
  if (!answer || answer.servicesUnavailable) { return { state: null, entry: null }; }
  const entry = serviceEntryFor(answer.services, serviceName);
  if (!entry) { return { state: 'absent', entry: null }; }
  if (isStoppedOnPurpose(entry)) { return { state: 'stopped', entry }; }
  return { state: isPreviewReady(entry) ? 'ready' : serviceState(entry), entry };
}

/**
 * What Canvas's preview loader (its `[service, project, revision, force, activeTab]` effect) does when it opens a
 * service, decided from the store's real answer (previewRunStateFor, always fresh). Opening the Preview NEVER starts a
 * service (2026-11-04: a service starts only when someone presses Start / Restart / Retry / Start all, from the CLI or
 * an MCP tool), so without `explicit` there is no `render`:
 * - `stopped`: show the stopped state with Start, whatever else holds (a forced fetch, a "pending initial prompt" left
 *   in sessionStorage, which survives a reload of the tab, no tab or entry yet). `notStarted` is true when the runner
 *   has no session at all (never run, reaped, a sandbox that woke up) or vfs-service cannot tell: "not running";
 * - `failed`: the last build failed or the app crashed: say so (with Retry), never build it again on its own;
 * - `show`: already running and ready, show it without a render;
 * - `follow`: already building or starting (someone's Start, the agent): follow that build until it is ready, never
 *   post a render;
 * - `render`: only with `explicit` (a person's Retry/Refresh), for a service that is not already running or building.
 * @param {{ projectId: string, serviceName: string, explicit?: boolean, timeoutMs?: number, retryMs?: number }} input
 *   (`force` and `pendingInitialPrompt` are accepted and ignored: neither is someone asking to start the service)
 * @returns {Promise<{ action: 'stopped'|'failed'|'show'|'follow'|'render', state: (string|null), entry: (object|null),
 *   notStarted?: boolean }>}
 */
export async function decidePreviewOpen({ projectId, serviceName, explicit = false, timeoutMs, retryMs }) {
  const { state, entry } = await previewRunStateFor(projectId, serviceName, { maxAgeMs: 0, timeoutMs, retryMs });
  if (state === 'ready') { return { action: 'show', state, entry }; }
  if (state === 'building' || state === 'running') { return { action: 'follow', state, entry }; }
  if (explicit) { return { action: 'render', state, entry }; }
  if (state === 'failed' || state === 'crashed') { return { action: 'failed', state, entry }; }
  return { action: 'stopped', state, entry, notStarted: state !== 'stopped' };
}

export default previewRunStateFor;
